import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import MonthlyTotals from "./MonthlyTotals";
import { buildBudgetRollup, fmtMoney, fmtDate } from "./budgetHelpers";

/* ---- per-cell popup: monthly breakdown for a single (codes_id) row ---- */
function CellMonthlyModal({ row, ledger, onClose }) {
  const pseudoTxn = { codes_id: row.codes_id, pye_id: null };
  const fyRange = ledger?.cal_frm && ledger?.cal_to
    ? { from: ledger.cal_frm, to: ledger.cal_to }
    : null;
  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="budget-cell-modal">
      <div className="modal-card txn-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>Budget Item Detail</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="bcm-close">×</button>
        </div>
        <div className="modal-form">
          <div style={{ display: "flex", gap: 22, marginBottom: 10, alignItems: "baseline", flexWrap: "wrap" }}>
            <span>
              <span className="muted">Code:</span>{" "}
              <b>{row.codes_id}</b>{" · "}{row.codes_lit}
            </span>
            <span><span className="muted">Section:</span> <b>{row.cat_sec} · {row.cat_sub}</b></span>
          </div>
          {fyRange && ledger?.cal_seq ? (
            <MonthlyTotals
              calSeq={ledger.cal_seq}
              txn={pseudoTxn}
              fyRange={fyRange}
              hidePayeeRow={true}
              signInvert={Number(row.cat_sign) === 1}
            />
          ) : (
            <div className="muted">No calendar window resolved for this ledger.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- inline "Budget / Actual / Variance" tag (reused across levels) ---- */
function MoneyTriple({ budget, actual, gap = 14 }) {
  const variance = budget - actual;
  return (
    <>
      <span className="muted">Budget:</span>{" "}
      <b className={budget < 0 ? "neg" : ""}>{fmtMoney(budget)}</b>
      <span className="muted" style={{ marginLeft: gap }}>Actual:</span>{" "}
      <b className={actual < 0 ? "neg" : ""}>{fmtMoney(actual)}</b>
      <span className="muted" style={{ marginLeft: gap }}>Variance:</span>{" "}
      <b className={variance < 0 ? "neg" : ""}>{fmtMoney(variance)}</b>
    </>
  );
}

/* ---- single "cat_sub" block: header + table of rows ---- */
function BudgetSubGroup({ sub, catSec, onRowClick }) {
  return (
    <div className="budget-group" data-testid={`budget-sub-${sub.cat_sub}`}>
      <div className="budget-group-head">
        <span className="budget-sub">{sub.cat_sub || "(uncategorized)"}</span>
        <span className="budget-group-totals" data-testid={`budget-sub-subtotal-${sub.cat_sub}`}>
          <MoneyTriple budget={sub.sub_budget} actual={sub.sub_actual} />
        </span>
      </div>      <table className="list-table list-table-compact txn-table budget-table">
        <thead>
          <tr className="list-head-labels">
            <th>Description</th>
            <th style={{ width: 160, textAlign: "right" }}>Budget Amt</th>
            <th style={{ width: 160, textAlign: "right" }}>Actual</th>
            <th style={{ width: 160, textAlign: "right" }}>Variance</th>
          </tr>
        </thead>
        <tbody>
          {sub.rows.map((r) => {
            const actual = r.signed_actual ?? r.actual;
            const variance = r.signed_budget - actual;
            return (
              <tr
                key={r.cat_seq}
                data-testid={`budget-row-${r.codes_id}`}
                onClick={() => onRowClick({ ...r, cat_sec: catSec })}
                style={{ cursor: "pointer" }}
                title="Click to see monthly breakdown"
              >
                <td>{r.codes_lit}</td>
                <td className={`mono ${r.signed_budget < 0 ? "neg" : ""}`} style={{ textAlign: "right" }}>
                  {fmtMoney(r.signed_budget)}
                </td>
                <td className={`mono ${actual < 0 ? "neg" : ""}`} style={{ textAlign: "right" }}>
                  {fmtMoney(actual)}
                </td>
                <td className={`mono ${variance < 0 ? "neg" : ""}`} style={{ textAlign: "right" }}>
                  {fmtMoney(variance)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---- one "cat_sec" accordion (header + nested sub-groups) ---- */
function BudgetDesGroup({ des, isOpen, onToggle, onRowClick }) {
  return (
    <div
      className={`budget-des admin-section ${isOpen ? "open" : "closed"}`}
      data-testid={`budget-des-${des.cat_sec}`}
    >
      <button
        type="button"
        className="budget-des-head admin-section-header"
        data-testid={`budget-des-head-${des.cat_sec}`}
        onClick={() => onToggle(des.cat_sec)}
        aria-expanded={isOpen}
      >
        <span className="admin-caret">▾</span>
        <span className="budget-des-title">{des.cat_sec}</span>
        <span className="budget-des-totals-inline">
          <MoneyTriple budget={des.des_budget} actual={des.des_actual} />
        </span>
      </button>
      <div className="admin-section-body">
        {des.subs.map((sub, idx) => (
          <BudgetSubGroup
            key={`${des.cat_sec}::${sub.cat_sub || ""}::${idx}`}
            sub={sub}
            catSec={des.cat_sec}
            onRowClick={onRowClick}
          />
        ))}
      </div>
    </div>
  );
}

/* ---- header: Year (left) + Ledger (right-justified) ---- */
function BudgetHeader({ years, calSeq, onYearChange,
                       ledgers, lgrId, onLedgerChange }) {
  const selectedYear = years && years.find((y) => y.cal_seq === calSeq);
  return (
    <div className="budget-header">
      <div className="budget-header-left">
        <label className="budget-label" htmlFor="cal-select">Year</label>
        <select
          id="cal-select"
          className="fy-select"
          data-testid="budget-year"
          value={calSeq || ""}
          onChange={(e) => onYearChange(Number(e.target.value))}
        >
          {!years && <option>Loading…</option>}
          {years && years.map((y) => (
            <option key={y.cal_seq} value={y.cal_seq}>{y.cal_year}</option>
          ))}
        </select>
        {selectedYear && (
          <span className="fy-range muted" data-testid="budget-year-range">
            {fmtDate(selectedYear.cal_frm)} – {fmtDate(selectedYear.cal_to)}
          </span>
        )}
      </div>
      <div className="budget-header-right">
        <label className="budget-label" htmlFor="lgr-select">Ledger</label>
        <select
          id="lgr-select"
          className="fy-select"
          data-testid="budget-lgr"
          value={lgrId}
          onChange={(e) => onLedgerChange(e.target.value)}
        >
          {!ledgers && <option>Loading…</option>}
          {ledgers && ledgers.map((l) => (
            <option key={l.lgr_id} value={l.lgr_id}>{l.lgr_lit}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/* -------- main page -------- */
export default function Budget() {
  const [ledgers, setLedgers] = useState(null);
  const [years, setYears] = useState(null);
  const [lgrId, setLgrId] = useState("");
  const [calSeq, setCalSeq] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [popupRow, setPopupRow] = useState(null);
  const [openDes, setOpenDes] = useState(() => new Set());

  const toggleDes = (name) => setOpenDes((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  useEffect(() => {
    Promise.all([api.ledgers(), api.fiscalYears()])
      .then(([lgs, fys]) => {
        setLedgers(lgs);
        setYears(fys);
        setLgrId((prev) => (prev || (lgs.length ? lgs[0].lgr_id : prev)));
        setCalSeq((prev) => {
          if (prev != null || !fys.length) return prev;
          // Prefer the FY whose [cal_frm, cal_to] window contains today;
          // fall back to the most-recent year. Matches the Dashboard rule.
          const today = new Date().toISOString().slice(0, 10);
          const containingToday = fys.find(
            (r) => String(r.cal_frm) <= today && today <= String(r.cal_to)
          );
          return (containingToday || fys[0]).cal_seq;
        });
      })
      .catch((e) => setErr(e.message));
  }, []);

  useEffect(() => {
    if (!lgrId || calSeq == null) return;
    setLoading(true); setErr("");
    api.budget(lgrId, calSeq)
      .then((d) => {
        setData(d);
        setOpenDes(new Set(d.groups.map((g) => g.cat_sec || "(uncategorized)")));
      })
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [lgrId, calSeq]);

  const rollup = useMemo(() => data ? buildBudgetRollup(data.groups) : null, [data]);
  const isEmpty = !loading && rollup && rollup.desList.length === 0;
  const hasRows = !loading && rollup && rollup.desList.length > 0;

  return (
    <>
      <section>
        <p className="section-title">Budgets</p>
        <div className="card budget-card" data-testid="budget-card">
          <BudgetHeader
            years={years}
            calSeq={calSeq}
            onYearChange={setCalSeq}
            ledgers={ledgers}
            lgrId={lgrId}
            onLedgerChange={setLgrId}
          />

          {err && <div className="form-error" style={{ margin: "12px 18px" }}>{err}</div>}
          {loading && <div className="loading-row"><span className="spinner" /> Loading budget…</div>}

          {isEmpty && (
            <div className="empty" data-testid="budget-empty">
              No category rows defined for this ledger.
            </div>
          )}

          {hasRows && rollup.desList.map((des) => (
            <BudgetDesGroup
              key={des.cat_sec}
              des={des}
              isOpen={openDes.has(des.cat_sec)}
              onToggle={toggleDes}
              onRowClick={setPopupRow}
            />
          ))}

          {hasRows && (
            <div className="budget-grand-totals" data-testid="budget-grand">
              <span className="budget-sub">Grand Total</span>
              <span>
                <MoneyTriple budget={rollup.grand_budget} actual={rollup.grand_actual} gap={18} />
              </span>
            </div>
          )}
        </div>
      </section>

      {popupRow && (
        <CellMonthlyModal row={popupRow} ledger={data?.ledger} onClose={() => setPopupRow(null)} />
      )}
    </>
  );
}
